import { create } from 'zustand';
import { persist, createJSONStorage, type StateStorage } from 'zustand/middleware';
import axios from 'axios';
import { CustomerResponse, CustomerAuthResponse } from '@/types';

interface AuthState {
    user: CustomerResponse | null;
    accessToken: string | null;
    refreshToken: string | null;
    tokenExpiresAt: number | null;
    isAuthenticated: boolean;
    isRefreshing: boolean;
    /**
     * Whether the current session should survive a browser close. Drives both the local
     * storage backend (localStorage when true, sessionStorage when false) and is sent to
     * the backend on refresh so it can keep the customer refresh-token TTL aligned with
     * the user's choice (30 days vs ~1 day per appsettings).
     */
    rememberMe: boolean;

    setAuth: (
        user: CustomerResponse,
        token: string,
        refreshToken: string,
        expiresAt: string,
        rememberMe?: boolean
    ) => void;
    logout: () => void;
    updateUser: (user: CustomerResponse) => void;
    refreshTokens: () => Promise<boolean>;
}

const AUTH_STORAGE_KEY = 'stork-auth-storage';

/**
 * Storage adapter that routes the persisted state to either localStorage (when the
 * persisted blob has rememberMe=true) or sessionStorage (when false). Reads consult
 * sessionStorage first so an ephemeral session can't be silently extended by a stale
 * localStorage copy from a previous browser session. Writes always clear the OTHER
 * store to prevent dual persistence.
 */
const dualStorage: StateStorage = {
    getItem: (name) => {
        if (typeof window === 'undefined') return null;
        return window.sessionStorage.getItem(name) ?? window.localStorage.getItem(name);
    },
    setItem: (name, value) => {
        if (typeof window === 'undefined') return;
        let persistent = true; // Default to persistent when we can't determine.
        try {
            const parsed = JSON.parse(value) as { state?: { rememberMe?: boolean } };
            persistent = parsed.state?.rememberMe ?? true;
        } catch {
            // Malformed payload: prefer persistent so we don't accidentally drop the session
            // on a parse error in production.
        }
        if (persistent) {
            window.localStorage.setItem(name, value);
            window.sessionStorage.removeItem(name);
        } else {
            window.sessionStorage.setItem(name, value);
            window.localStorage.removeItem(name);
        }
    },
    removeItem: (name) => {
        if (typeof window === 'undefined') return;
        window.localStorage.removeItem(name);
        window.sessionStorage.removeItem(name);
    },
};

export const useAuthStore = create<AuthState>()(
    persist(
        (set, get) => ({
            user: null,
            accessToken: null,
            refreshToken: null,
            tokenExpiresAt: null,
            isAuthenticated: false,
            isRefreshing: false,
            // Default true preserves the historical "always persist" behavior for code paths
            // that don't yet pass rememberMe explicitly (e.g. legacy registration callers).
            rememberMe: true,

            setAuth: (user, token, refreshToken, expiresAt, rememberMe) => {
                const expiryTimestamp = new Date(expiresAt).getTime();

                set({
                    user,
                    accessToken: token,
                    refreshToken,
                    tokenExpiresAt: expiryTimestamp,
                    isAuthenticated: true,
                    // Preserve the prior choice if the caller didn't supply one. This way
                    // post-refresh updates that don't know rememberMe (refresh response) keep
                    // the original setting.
                    rememberMe: rememberMe ?? get().rememberMe,
                });
            },

            logout: () => {
                set({
                    user: null,
                    accessToken: null,
                    refreshToken: null,
                    tokenExpiresAt: null,
                    isAuthenticated: false,
                    // Reset to default; next login decides anew.
                    rememberMe: true,
                });
            },

            updateUser: (user) => set({ user }),

            refreshTokens: async () => {
                const state = get();

                if (!state.refreshToken) {
                    return false;
                }

                if (state.isRefreshing) {
                    return false;
                }

                set({ isRefreshing: true });

                try {
                    // Call the refresh API directly with base axios
                    const baseURL = import.meta.env.VITE_API_URL || '/api';
                    const response = await axios.post<CustomerAuthResponse>(
                        `${baseURL}/customer/auth/refresh`,
                        {
                            refreshToken: state.refreshToken,
                            // Echo the original choice so the backend re-issues a refresh
                            // token with the matching TTL.
                            rememberMe: state.rememberMe,
                        },
                        {
                            headers: { 'Content-Type': 'application/json' },
                        }
                    );

                    const data = response.data;

                    if (!data.accessToken || !data.refreshToken) {
                        throw new Error('Invalid refresh response');
                    }

                    const expiryTimestamp = new Date(data.expiresAt).getTime();

                    set({
                        accessToken: data.accessToken,
                        refreshToken: data.refreshToken,
                        tokenExpiresAt: expiryTimestamp,
                        user: data.customer,
                        isRefreshing: false,
                    });

                    return true;
                } catch {
                    set({ isRefreshing: false });

                    // Clear tokens on failure
                    get().logout();
                    return false;
                }
            },
        }),
        {
            name: AUTH_STORAGE_KEY,
            storage: createJSONStorage(() => dualStorage),
        }
    )
);
